import FindControlViewState from 'owa-recipient-types/lib/types/FindControlViewState';
import { createPeopleFeedback } from 'owa-recipient-types/lib/types/PeopleFeedbackState';
import PersonaType from 'owa-service/lib/contract/PersonaType';
import { OwaDate, getUTCString } from 'owa-datetime';
import shouldUse3SPeopleSuggestions from 'owa-recipient-suggestions/lib/util/shouldUse3SPeopleSuggestions';

const CACHE_SOURCE = 'Cache';
const MAILBOX_SOURCE = 'Mailbox';
const DIRECTORY_SOURCE = 'Directory';
const FALLBACK_SOURCE = ' Fallback';

/**
 * Resets the feedback state
 * @param recipientWell the recipient well view state
 */
export function resetFeedbackState(recipientWell: FindControlViewState): void {
    recipientWell.peopleFeedbackState = { live: null, cache: null, directory: null };
}

/**
 * Used to document the beginning of the outgoing people search request to the feedback state
 * @param recipientWell the recipient well view state
 * @param transactionStartTime the start time of the outgoing request
 * @param clientRequestId the client request id for the outgoing request
 * @param directory whether it is a directory call
 */
export function populateFeedbackStateNetwork(
    recipientWell: FindControlViewState,
    transactionStartTime: OwaDate,
    correlationId: string,
    directory: boolean
): void {
    if (shouldUse3SPeopleSuggestions() && recipientWell.peopleFeedbackState) {
        let feedback = createPeopleFeedback();
        feedback.TimeStamp = getUTCString(transactionStartTime);
        feedback.QueryString = recipientWell.queryString;
        feedback.SuggestionSource = directory ? DIRECTORY_SOURCE : MAILBOX_SOURCE;

        feedback.RawResponse = 'pending';

        if (correlationId) {
            feedback.CorrelationId = correlationId;
        }

        if (directory) {
            recipientWell.peopleFeedbackState.directory = feedback;
        } else {
            recipientWell.peopleFeedbackState.live = feedback;
        }
    }
}

/**
 * Used to document the return/result of the people search response in the feedback state
 * @param recipientWell the recipient well view state
 * @param directory whether it is a directory call
 * @param response the people search response
 */
export function updateFeedbackStateNetwork(
    recipientWell: FindControlViewState,
    directory: boolean,
    response: object
): void {
    if (shouldUse3SPeopleSuggestions() && recipientWell.peopleFeedbackState) {
        const rawResponse = response ? JSON.stringify(response) : 'no response';

        if (recipientWell && recipientWell.peopleFeedbackState) {
            if (directory && recipientWell.peopleFeedbackState.directory) {
                recipientWell.peopleFeedbackState.directory.RawResponse = rawResponse;
            } else if (!directory && recipientWell.peopleFeedbackState.live) {
                recipientWell.peopleFeedbackState.live.RawResponse = rawResponse;
            }
        }
    }
}

/**
 * Used to populate the result of a cache lookup in the feedback state
 * @param recipientWell the recipient well view state
 * @param transactionStartTime the cache lookup start time
 * @param cacheResults the cache results
 * @param fallback whether this was a cache fallback
 */
export function populateFeedbackStateCache(
    recipientWell: FindControlViewState,
    transactionStartTime: OwaDate,
    cacheResults: PersonaType[],
    fallback?: boolean
): void {
    if (shouldUse3SPeopleSuggestions() && recipientWell.peopleFeedbackState) {
        let feedback = createPeopleFeedback();
        feedback.QueryString = recipientWell.queryString;
        feedback.TimeStamp = getUTCString(transactionStartTime);
        feedback.SuggestionSource = fallback ? CACHE_SOURCE + FALLBACK_SOURCE : CACHE_SOURCE;
        feedback.RawResponse = cacheResults ? JSON.stringify(cacheResults) : 'no response';

        recipientWell.peopleFeedbackState.cache = feedback;
    }
}
